import { useQuery } from '@tanstack/react-query';
import { staffService } from '@/services/api/staff.service';
import { useSetPageHeader } from '@/components/chrome/PageHeader';
import { Spinner } from '@/components/ui/spinner';
import { StatsCard } from '@/components/staff/StatsCard';
import { AppointmentCard } from '@/components/staff/AppointmentCard';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import {
  Calendar,
  TrendingUp,
  Clock,
  ArrowRight,
} from 'lucide-react';
import { format } from 'date-fns';
import { getDateLocale, getDateFormats } from '@/utils/dateLocale';

export default function StaffDashboardPage() {
  const { t } = useTranslation(['staff', 'common']);
  const [, setLocation] = useLocation();

  useSetPageHeader(
    {
      sub: t('dashboard.subtitle'),
      actions: (
        <div className="flex gap-3">
          <Link href="/staff/schedule">
            <Button variant="outline" size="md" iconLeft={<Clock className="h-4 w-4" />}>
              {t('dashboard.quickActions.viewSchedule')}
            </Button>
          </Link>
          <Link href="/staff/bookings">
            <Button size="md" iconLeft={<Calendar className="h-4 w-4" />}>
              {t('dashboard.quickActions.viewAllBookings')}
            </Button>
          </Link>
        </div>
      ),
    },
    [t],
  );

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['staff-dashboard-stats'],
    queryFn: () => staffService.getDashboardStats(),
  });

  const { data: todayAppointments, isLoading: todayLoading } = useQuery({
    queryKey: ['staff-today-appointments'],
    queryFn: () => staffService.getTodayAppointments(),
  });

  const { data: upcomingAppointments, isLoading: upcomingLoading } = useQuery({
    queryKey: ['staff-upcoming-appointments'],
    queryFn: () => staffService.getUpcomingAppointments(),
  });

  if (statsLoading || todayLoading || upcomingLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const statsCards = [
    {
      title: t('dashboard.stats.totalBookings'),
      value: stats?.total_bookings || 0,
      icon: Calendar,
      iconBgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
    },
  ];

  // Transform API response to component format
  const todayBookingsData = todayAppointments?.bookings || [];
  const upcomingDates = upcomingAppointments?.dates || [];

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-4">
        {statsCards.map((stat, index) => (
          <StatsCard key={index} {...stat} />
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today's Schedule - 2/3 width */}
        <div className="lg:col-span-2 bg-surface rounded-lg border border-line">
          <div className="px-6 py-4 border-b border-line">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-h3 text-ink">
                  {t('dashboard.todaySchedule.title')}
                </h2>
                <p className="text-sm text-soft mt-0.5">
                  {format(new Date(), getDateFormats().fullWithWeekday, { locale: getDateLocale() })}
                </p>
              </div>
              <div className="h-10 w-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <Calendar className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {todayBookingsData && todayBookingsData.length > 0 ? (
                todayBookingsData.map((booking, index) => (
                  <AppointmentCard
                    key={booking.id}
                    customerName={booking.customer_full_name}
                    serviceName={booking.service?.name || 'Unknown Service'}
                    startTime={booking.start_time}
                    endTime={booking.end_time}
                    status={booking.status as any}
                    notes={booking.notes}
                    isNext={index === 0}
                    onClick={() => setLocation(`/staff/bookings/${booking.public_id}`)}
                  />
                ))
              ) : (
                <div className="text-center py-12">
                  <Calendar className="h-12 w-12 text-soft mx-auto mb-3" />
                  <p className="text-sm text-soft">
                    {t('dashboard.todaySchedule.noAppointments')}
                  </p>
                  <p className="text-xs text-soft mt-1">
                    Enjoy your day!
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Upcoming Appointments - 1/3 width */}
        <div className="bg-surface rounded-lg border border-line">
          <div className="px-6 py-4 border-b border-line">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-h3 text-ink">
                  {t('dashboard.upcomingWeek.title')}
                </h2>
                <p className="text-sm text-soft mt-0.5">
                  {t('dashboard.upcomingWeek.subtitle')}
                </p>
              </div>
              <div className="h-10 w-10 bg-purple-50 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {upcomingDates && upcomingDates.length > 0 ? (
                <>
                  {upcomingDates.flatMap(dateGroup => dateGroup.bookings).slice(0, 5).map((booking) => (
                    <div
                      key={booking.id}
                      onClick={() => setLocation(`/staff/bookings/${booking.public_id}`)}
                      className="p-3 rounded-lg bg-surface-2 hover:bg-surface-2 transition-colors border border-line cursor-pointer"
                    >
                      <div className="flex items-start justify-between mb-1">
                        <p className="font-medium text-ink text-sm truncate">
                          {booking.customer_full_name}
                        </p>
                      </div>
                      <p className="text-xs text-soft mb-1">
                        {booking.service?.name || 'Unknown Service'}
                      </p>
                      <p className="text-xs text-soft">
                        {format(new Date(booking.start_time), getDateFormats().dayMonth, { locale: getDateLocale() })}, {format(new Date(booking.start_time), 'HH:mm')}
                      </p>
                    </div>
                  ))}
                  <Link href="/staff/bookings">
                    <Button variant="ghost" size="sm" className="w-full mt-2">
                      View All
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </Link>
                </>
              ) : (
                <div className="text-center py-8">
                  <TrendingUp className="h-10 w-10 text-soft mx-auto mb-2" />
                  <p className="text-sm text-soft">
                    {t('dashboard.upcomingWeek.noAppointments')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
